import type { LucideIcon } from 'lucide-react';

export type DifficultyColorTier = 'gentle' | 'balanced' | 'advanced';

export interface DifficultyTileProps {
  readonly level: string;
  readonly icon: LucideIcon;
  readonly label: string;
  readonly colorTier: DifficultyColorTier;
  readonly selected: boolean;
  readonly onClick: () => void;
  readonly className?: string;
}

const TIER_RING: Record<DifficultyColorTier, string> = {
  gentle: 'ring-mint',
  balanced: 'ring-sky',
  advanced: 'ring-berry',
};

const TIER_TINT: Record<DifficultyColorTier, string> = {
  gentle: 'bg-mint/15',
  balanced: 'bg-sky/15',
  advanced: 'bg-berry/15',
};

const TIER_ICON: Record<DifficultyColorTier, string> = {
  gentle: 'text-mint',
  balanced: 'text-sky',
  advanced: 'text-berry',
};

export function DifficultyTile({
  level,
  icon: Icon,
  label,
  colorTier,
  selected,
  onClick,
  className = '',
}: DifficultyTileProps) {
  return (
    <button
      type="button"
      // biome-ignore lint/a11y/useSemanticElements: visual design requires button styling + keyboard activation; radio role is correct inside a role="radiogroup" parent (RobotPicker)
      role="radio"
      aria-checked={selected}
      aria-label={`Difficulty: ${label}`}
      data-testid={`difficulty-tile-${level}`}
      onClick={onClick}
      className={`flex h-20 w-20 flex-col sm:h-24 sm:w-24 lg:h-28 lg:w-28 items-center justify-center gap-1 rounded-2xl border-2 border-transparent bg-surface text-fg shadow-sm transition-all hover:brightness-105 active:scale-95 ${
        selected ? `ring-2 ${TIER_RING[colorTier]} ${TIER_TINT[colorTier]}` : ''
      } ${className}`}
    >
      <Icon className={`h-7 w-7 lg:h-9 lg:w-9 ${TIER_ICON[colorTier]}`} aria-hidden />
      <span className="text-sm font-bold lg:text-base">{label}</span>
    </button>
  );
}
